import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMemoizedFn, useMount } from 'ahooks';
import { Select, Tooltip, TreeSelect } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import {
  MultiTypeEnum,
  SKU,
  SKU_SERVICE_GENRE_ENUM,
  SKU_SERVICE_TYPE_ENUM,
  SvcShowTypeEnum,
} from 'domains/skuDomain';
import { cloneDeep, flattenDeep } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import svcService from 'services/svcService';
import './SvcTree.less';
export interface SvcTreeValue {
  svcGroupLevel?: SKU_SERVICE_TYPE_ENUM;
  svcGenre?: SKU_SERVICE_GENRE_ENUM;
  svcType?: SvcShowTypeEnum;
  ids?: string[];
}
interface SvcTreeProps {
  value?: SvcTreeValue;
  onChange?: (value: SvcTreeValue) => void;
  sku?: Partial<SKU>;
  fields?: FormListFieldData[];
  remove?: (index: number | number[]) => void;
  index?: number;
  required?: boolean;
  option?: boolean;
  showType: SvcShowTypeEnum;
  disabled: boolean;
}

export const SvcTree = (props: SvcTreeProps) => {
  const {
    remove,
    index,
    sku,
    option,
    required,
    value,
    onChange,
    showType,
    disabled,
  } = props;
  const [treeSvc, setTreeSvc] = useState<any>([]);
  // const [allItemList, setAllItemList] = useState<any[]>([]);
  const [svcGroupLevel, setSvcGroupLevel] = useState<SKU_SERVICE_TYPE_ENUM>();
  const [svcGenre, setSvcGenre] = useState<SKU_SERVICE_GENRE_ENUM>();
  const [svcType, setSvcType] = useState<SvcShowTypeEnum>();
  const [loading, setLoading] = useState(false);
  const [treeSelectValue, setTreeSelectValue] = useState<string[]>([]);

  useMount(async () => {
    const res = await svcService.getSvcTree();
    setTreeSvc(res);
    // const treeDataSvc = defaultTreeData(treeSvc);
    // setTreeData(treeDataSvc.list);
    // setAllItemList(treeDataSvc.allKeyList);
  });

  useEffect(() => {
    value?.svcGroupLevel && setSvcGroupLevel(value?.svcGroupLevel);
    value?.svcGenre && setSvcGenre(value?.svcGenre);
    // value?.svcType && setSvcType(value?.svcType);
    value?.ids && setTreeSelectValue(value?.ids);
  }, [value]);

  useEffect(() => {
    setSvcType(showType);
    onChangeHandle({
      ...value,
      svcType: showType,
    });
    // eslint-disable-next-line
  }, [showType]);

  const isWrapper = useMemo(() => {
    return (
      sku?.multiType &&
      [
        MultiTypeEnum.WRAPPER,
        MultiTypeEnum.STANDALONE,
        MultiTypeEnum.BOTH,
      ].includes(sku?.multiType)
    );
  }, [sku]);

  const isPart = useMemo(() => {
    return (
      sku?.multiType &&
      [MultiTypeEnum.PART, MultiTypeEnum.STANDALONE].includes(sku?.multiType)
    );
  }, [sku]);

  const svcTypeOption = useMemo(() => {
    const returnData = [];
    if (isWrapper && required) {
      returnData.push({
        title: 'Job Option',
        value: SvcShowTypeEnum.JOB_OPTIONS,
      });
    }
    if (isPart && required) {
      returnData.push({
        title: 'Required',
        value: SvcShowTypeEnum.REQUIRED,
      });
    }
    if (isWrapper && option) {
      returnData.push({
        title: 'Additional Job Options',
        value: SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS,
      });
    }
    if (isPart && option) {
      returnData.push({
        title: 'Option',
        value: SvcShowTypeEnum.OPTIONAL,
      });
    }
    return returnData;
  }, [isWrapper, isPart, required, option]);

  const SvcGroupLevelOption = [
    {
      title: 'Service Type',
      value: SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE,
    },
    {
      title: 'Service',
      value: SKU_SERVICE_TYPE_ENUM.SERVICE,
    },
    {
      title: 'Service Spec',
      value: SKU_SERVICE_TYPE_ENUM.SERVICE_SPEC,
    },
  ];

  const svcGenreOption = useMemo(() => {
    if (svcGroupLevel === SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE) {
      return [
        {
          title: 'Any Of',
          value: SKU_SERVICE_GENRE_ENUM.ANY_OF,
        },
      ];
    }
    return [
      {
        title: 'One Of',
        value: SKU_SERVICE_GENRE_ENUM.ONE_OF,
      },
      {
        title: 'Any Of',
        value: SKU_SERVICE_GENRE_ENUM.ANY_OF,
      },
    ];
  }, [svcGroupLevel]);

  // const svcTypeOption = [
  //   {
  //     title: 'Additional Job Options',
  //     value: SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS,
  //   },
  //   {
  //     title: 'Job Option',
  //     value: SvcShowTypeEnum.JOB_OPTIONS,
  //   },
  //   {
  //     title: 'Option',
  //     value: SvcShowTypeEnum.OPTIONAL,
  //   },
  //   {
  //     title: 'Required',
  //     value: SvcShowTypeEnum.REQUIRED,
  //   },
  // ];

  const defaultTreeData = useMemoizedFn(
    (treeSvc: any, SvcGroupLevel: SKU_SERVICE_TYPE_ENUM) => {
      let allKeyList: any[] = [];
      treeSvc.forEach((item: any) => {
        item.children = item.svcNodes;
        item.title = item.typeName;
        item.key = `type${item.typeId}`;
        // item.key = item.typeId;
        item.fKey = [];
        if (SvcGroupLevel === SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE) {
          item.children = [];
          item.disabled = false;
        } else {
          item.disabled = true;
          item.children.forEach((cItem: any) => {
            cItem.children = cItem.specNodes;
            cItem.title = cItem.svcName;
            cItem.key = `svc${cItem.svcId}`;
            // cItem.key = cItem.svcId;
            cItem.fKey = [`type${item.typeId}`];
            if (SvcGroupLevel === SKU_SERVICE_TYPE_ENUM.SERVICE) {
              cItem.children = [];
              cItem.cKey = '';
              cItem.disabled = false;
            } else {
              cItem.disabled = true;
              cItem.children.forEach((ccItem: any) => {
                ccItem.title = ccItem.description;
                ccItem.key = `spec${ccItem.specId}`;
                // ccItem.key = ccItem.specId;
                ccItem.fKey = [`type${item.typeId}`, `svc${cItem.svcId}`];
                ccItem.cKey = [];
                ccItem.disabled = false;
                allKeyList.push(ccItem);
              });
              cItem.cKey = cItem.children?.map((ccItem: any) => ccItem.key);
            }
            allKeyList.push(cItem);
          });
        }
        item.cKey = flattenDeep(
          item.children?.map((cItem: any) => {
            const keys = cItem.children?.map((ccItem: any) => ccItem.key);
            return [...keys, cItem.key];
          })
        );
        allKeyList.push(item);
      });
      return {
        list: treeSvc,
        allKeyList: allKeyList,
      };
    }
  );

  const onChangeHandle = (data: SvcTreeValue) => {
    if (typeof onChange === 'function') {
      onChange(data);
    }
  };

  const treeData = useMemo(() => {
    if (!svcGroupLevel) {
      return [];
    }
    const treeDataSvc = cloneDeep(defaultTreeData(treeSvc, svcGroupLevel));
    // setAllItemList(treeDataSvc.allKeyList);
    setTimeout(() => {
      setLoading(false);
    }, 500);
    return treeDataSvc?.list;
  }, [treeSvc, svcGroupLevel, defaultTreeData]);

  // const onCheck = useMemoizedFn((checkList: any, e: any) => {
  //   console.log('onCheck');
  //   // console.log(checkList, e, checkedKeys, e.node.fKey);
  //   // console.log(xor(checkedKeys, checkNodeList(e.checkedNodes)));
  //   // console.log(checkedKeys.indexOf(e.node.key));
  //   console.log(xor(checkedKeys, [e.node.key]), e.node.fKey);
  //   if (checkedKeys.indexOf(e.node.key) + 1) {
  //     setCheckedKeys(difference(xor(checkedKeys, [e.node.key]), e.node.fKey));
  //   } else {
  //     setCheckedKeys([
  //       ...checkedKeys,
  //       ...checkNodeList(e.node.children),
  //       e.node.key,
  //     ]);
  //   }

  //   setTimeout(() => submitTreeData());
  // });

  // const checkNodeList = (node: any[]) => {
  //   const list: string[] = [];
  //   node?.forEach((item) => {
  //     list.push(item.key);
  //     item.children?.forEach((cItem: any) => {
  //       list.push(cItem.key);
  //       cItem.children?.forEach((ccItem: any) => {
  //         list.push(ccItem.key);
  //       });
  //     });
  //   });
  //   return list;
  // };

  const onSelect = (selectedKeys: any, e: any) => {
    setTreeSelectValue(selectedKeys);
    onChangeHandle({
      ...value,
      ids: selectedKeys,
    });
  };

  // const onExpand = (expandedKeys: any, e: any) => {
  //   console.log('onExpand');
  //   console.log(expandedKeys, e);
  // };

  return (
    <>
      <div className="skuSvc-config">
        <RevxFlexLayout className="skuSvc-config-header">
          <Select
            value={svcGroupLevel}
            disabled={disabled}
            onChange={(e) => {
              setSvcGroupLevel(e);
              setLoading(true);
              setTreeSelectValue([]);
              setSvcGenre(
                e === SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE
                  ? SKU_SERVICE_GENRE_ENUM.ANY_OF
                  : svcGenre
                  ? svcGenre
                  : SKU_SERVICE_GENRE_ENUM.ANY_OF
              );
              onChangeHandle({
                ...value,
                svcGroupLevel: e,
                ids: [],
                svcGenre:
                  e === SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE
                    ? SKU_SERVICE_GENRE_ENUM.ANY_OF
                    : svcGenre
                    ? svcGenre
                    : SKU_SERVICE_GENRE_ENUM.ANY_OF,
              });
            }}
          >
            {SvcGroupLevelOption.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.title}
              </Select.Option>
            ))}
          </Select>
          <Select
            className="skuSvc-config-header-right"
            value={svcGenre}
            disabled={disabled}
            onChange={(e) => {
              setSvcGenre(e);
              onChangeHandle({
                ...value,
                svcGenre: e,
              });
            }}
          >
            {svcGenreOption.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.title}
              </Select.Option>
            ))}
          </Select>
          {svcGenre === SKU_SERVICE_GENRE_ENUM.ANY_OF && (
            <Tooltip
              title="The same service groups will be merged"
              overlayStyle={{ minWidth: '254px' }}
            >
              <QuestionCircleOutlined style={{ fontSize: 15, marginLeft: 8 }} />
            </Tooltip>
          )}
          <Select
            className="skuSvc-config-header-right"
            value={svcType}
            disabled
          >
            {svcTypeOption.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.title}
              </Select.Option>
            ))}
          </Select>
          <RevxButton
            className="skuSvc-config-header-right"
            brandGreen
            disabled={disabled}
            onClick={() => {
              if (typeof remove === 'function' && typeof index === 'number') {
                remove(index);
              }
            }}
          >
            remove
          </RevxButton>
        </RevxFlexLayout>
        {Boolean(treeData.length && !loading) && (
          <TreeSelect
            disabled={disabled}
            className="skuSvc-config-treeSelect"
            showSearch
            filterTreeNode={(input, option) => {
              if (input === ' ') {
                return true;
              }
              return Boolean(
                option?.title
                  ?.toLocaleString()
                  ?.toLowerCase()
                  ?.includes(input?.trim()?.toLowerCase())
              );
            }}
            value={treeSelectValue}
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder="Please select"
            allowClear
            multiple
            treeDefaultExpandAll
            onChange={onSelect}
            treeData={treeData}
          />
        )}
      </div>
    </>
  );
};
